import { useCallback, useEffect, useState } from "react";
import { X, Plus, Edit, Trash2, Check, Loader2, Tags } from "lucide-react";
import { toast } from "sonner";

import { categoryService } from "@/services/blogService";

const pick = (r) => r?.data ?? r;

export default function CategoryManager({ onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = pick(await categoryService.getAll());
      setItems(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("LOAD CATEGORIES ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function run(action, successMsg, failMsg) {
    try {
      setBusy(true);
      await action();
      toast.success(successMsg);
      await load();
      onChanged?.();
      return true;
    } catch (error) {
      console.error("CATEGORY ERROR:", error);
      toast.error(error?.response?.data?.message || failMsg);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    const value = name.trim();
    if (!value) return toast.error("Category name is required");

    const ok = await run(
      () => categoryService.create(value),
      "Category created",
      "Failed to create category"
    );
    if (ok) setName("");
    return undefined;
  }

  async function handleSaveEdit(id) {
    const value = editName.trim();
    if (!value) return toast.error("Category name is required");

    const ok = await run(
      () => categoryService.update(id, value),
      "Category updated",
      "Failed to update category"
    );
    if (ok) setEditingId(null);
    return undefined;
  }

  async function handleDelete(item) {
    const confirmed = window.confirm(`Delete category "${item.name}"?`);
    if (!confirmed) return;

    await run(
      () => categoryService.delete(item._id),
      "Category deleted",
      "Failed to delete category"
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-5">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          if (!busy) onClose();
        }}
      />

      <div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-[#F5A623]" />
            <h2 className="font-heading text-lg font-bold text-slate-900 sm:text-xl">
              Blog Categories
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ADD */}
        <form
          onSubmit={handleAdd}
          className="flex flex-col gap-2 border-b border-slate-200 p-4 sm:flex-row sm:p-5"
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New category name"
            className="w-full flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-4 py-2.5 text-sm font-bold text-slate-950 hover:bg-[#ffb638] disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </form>

        {/* LIST */}
        <div className="overflow-y-auto p-4 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading...
            </div>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No categories yet.</p>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => (
                <li
                  key={item._id}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5"
                >
                  {editingId === item._id ? (
                    <>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-[#F5A623]"
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(item._id)}
                        disabled={busy}
                        className="rounded-lg p-2 text-green-600 hover:bg-green-50 disabled:opacity-50"
                        title="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                        title="Cancel"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">{item.name}</p>
                        <p className="truncate text-xs text-slate-400">
                          /{item.slug} · {item.blogCount || 0} blog(s)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(item._id);
                          setEditName(item.name);
                        }}
                        className="rounded-lg p-2 text-blue-500 hover:bg-blue-50"
                        title="Rename"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        disabled={busy}
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}